import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { 
  Trophy, Target, Shield, Brain, Scale, Leaf, CheckCircle2,
  Award, Clock, Users, ChevronRight, Loader2, Play,
  ShieldCheck, ShieldAlert, AlertTriangle, Flame, Zap, Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

// Campaign type icons
const CAMPAIGN_ICONS = {
  compliance: ShieldCheck,
  cyber_awareness: ShieldAlert,
  ai_governance: Brain,
  ethics: Scale,
  risk_management: Target,
  esg: Leaf,
  mandatory_training: CheckCircle2,
  custom: Award,
};

const CAMPAIGN_COLORS = {
  compliance: { bg: "from-blue-600 to-blue-700", accent: "text-blue-400", border: "border-blue-500/50" },
  cyber_awareness: { bg: "from-[#1EBFB3] to-purple-700", accent: "text-[#1EBFB3]", border: "border-[#1EBFB3]/50" },
  ai_governance: { bg: "from-cyan-600 to-cyan-700", accent: "text-cyan-400", border: "border-cyan-500/50" },
  ethics: { bg: "from-green-600 to-green-700", accent: "text-green-400", border: "border-green-500/50" },
  risk_management: { bg: "from-amber-600 to-amber-700", accent: "text-amber-400", border: "border-amber-500/50" },
  esg: { bg: "from-emerald-600 to-emerald-700", accent: "text-emerald-400", border: "border-emerald-500/50" },
  mandatory_training: { bg: "from-red-600 to-red-700", accent: "text-red-400", border: "border-red-500/50" },
  custom: { bg: "from-navigate-gold to-amber-600", accent: "text-navigate-gold", border: "border-navigate-gold/50" },
};

// Campaign Card Component
function CampaignCard({ campaign, onStart, onView }) {
  const Icon = CAMPAIGN_ICONS[campaign.campaign_type] || Award;
  const colors = CAMPAIGN_COLORS[campaign.campaign_type] || CAMPAIGN_COLORS.custom;
  const isStarted = campaign.user_started;
  const isCompleted = campaign.user_completed;
  const progress = campaign.user_progress_percent || 0;
  
  return (
    <div 
      className={`bg-zinc-900/70 border ${colors.border} rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300`}
      data-testid={`campaign-card-${campaign.campaign_id}`}
    >
      {/* Header with gradient */}
      <div className={`bg-gradient-to-r ${colors.bg} p-5`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">{campaign.name}</h3>
              <p className="text-sm text-white/70">{campaign.activities?.length || 0} activities</p>
            </div>
          </div>
          {isCompleted && (
            <div className="bg-white/20 px-3 py-1 rounded-full flex items-center gap-1">
              <Trophy className="h-4 w-4 text-yellow-300" />
              <span className="text-xs font-medium text-white">Complete</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Body */}
      <div className="p-5">
        <p className="text-sm text-zinc-400 line-clamp-2 mb-4">{campaign.description}</p>
        
        {/* Progress */}
        {isStarted && !isCompleted && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-zinc-500">Progress</span>
              <span className={colors.accent}>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}
        
        {/* Rewards */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-navigate-gold" />
            <span className="text-sm text-zinc-300">+{campaign.bonus_xp} XP</span>
          </div>
          <div className="flex items-center gap-2">
            <Award className={`h-4 w-4 ${colors.accent}`} />
            <span className="text-sm text-zinc-300">Badge</span>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2">
          {!isStarted ? (
            <Button
              onClick={() => onStart(campaign.campaign_id)}
              className={`flex-1 bg-gradient-to-r ${colors.bg} text-white hover:opacity-90`}
              data-testid={`start-campaign-${campaign.campaign_id}`}
            >
              <Play className="h-4 w-4 mr-2" />
              Start Campaign
            </Button>
          ) : isCompleted ? (
            <Button
              onClick={() => onView(campaign.campaign_id)}
              variant="outline"
              className="flex-1 border-zinc-700 text-zinc-300"
            >
              <Trophy className="h-4 w-4 mr-2 text-yellow-400" />
              View Certificate
            </Button>
          ) : (
            <Button
              onClick={() => onView(campaign.campaign_id)}
              className={`flex-1 bg-gradient-to-r ${colors.bg} text-white hover:opacity-90`}
            >
              Continue
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Campaign Detail Modal
function CampaignDetailModal({ campaign, progress, onClose, onActivityComplete }) {
  const navigate = useNavigate();
  const Icon = CAMPAIGN_ICONS[campaign.campaign_type] || Award;
  const colors = CAMPAIGN_COLORS[campaign.campaign_type] || CAMPAIGN_COLORS.custom;
  const completedActivities = progress?.completed_activities || [];
  
  const handleActivityClick = (activity) => {
    // Navigate to the appropriate content
    if (activity.activity_type === 'course') {
      navigate(`/courses/${activity.reference_id}/ai-learn`);
    } else if (activity.activity_type === 'learning_lab') {
      navigate(`/learning-lab/${activity.reference_id}`);
    } else if (activity.activity_type === 'video') {
      navigate(`/videos/${activity.reference_id}`);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${colors.bg} p-6`}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">{campaign.name}</h2>
              <p className="text-white/70">{campaign.description}</p>
            </div>
          </div>
          
          {/* Progress bar */}
          {progress && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-white/70">
                  {completedActivities.length} of {campaign.activities?.length || 0} completed
                </span>
                <span className="text-white font-medium">{Math.round(progress.progress_percent)}%</span>
              </div>
              <Progress value={progress.progress_percent} className="h-3 bg-white/20" />
            </div>
          )}
        </div>
        
        {/* Activities List */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          <h3 className="text-lg font-semibold text-white mb-4">Campaign Activities</h3>
          <div className="space-y-3">
            {campaign.activities?.map((activity, idx) => {
              const isComplete = completedActivities.includes(activity.activity_id);
              
              return (
                <div
                  key={activity.activity_id}
                  className={`p-4 rounded-xl border transition-colors cursor-pointer ${
                    isComplete 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                  }`}
                  onClick={() => handleActivityClick(activity)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isComplete ? 'bg-green-500/20' : 'bg-zinc-700'
                      }`}>
                        {isComplete ? (
                          <CheckCircle2 className="h-5 w-5 text-green-400" />
                        ) : (
                          <span className="text-sm font-medium text-zinc-400">{idx + 1}</span>
                        )}
                      </div>
                      <div>
                        <h4 className="font-medium text-white">{activity.title}</h4>
                        <p className="text-xs text-zinc-500 capitalize">{activity.activity_type.replace('_', ' ')}</p>
                      </div>
                    </div>
                    <ChevronRight className={`h-5 w-5 ${isComplete ? 'text-green-400' : 'text-zinc-500'}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-zinc-700 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-navigate-gold" />
              <span className="text-zinc-300">+{campaign.bonus_xp} XP on completion</span>
            </div>
          </div>
          <Button onClick={onClose} variant="outline" className="border-zinc-600">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

// Earned Badges Section
function EarnedBadgesSection({ badges }) {
  if (!badges || badges.length === 0) return null;
  
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 mb-8">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <Trophy className="h-5 w-5 text-navigate-gold" />
        Campaign Badges Earned
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {badges.map((badge) => {
          const template = badge.badge_template || {};
          return (
            <div 
              key={badge.badge_id}
              className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700 text-center"
            >
              <div 
                className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3"
                style={{ backgroundColor: `${template.color}20` }}
              >
                <Award className="h-8 w-8" style={{ color: template.color }} />
              </div>
              <h4 className="font-medium text-white text-sm">{template.name}</h4>
              <p className="text-xs text-zinc-500 mt-1">{badge.campaign_name}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Main Campaigns Page
export default function CampaignsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedProgress, setSelectedProgress] = useState(null);
  const [filter, setFilter] = useState('all'); // all, active, completed
  
  const fetchData = async () => {
    try {
      const [campaignsRes, badgesRes] = await Promise.all([
        axios.get(`${API}/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/campaigns/badges/earned`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      setEarnedBadges(badgesRes.data.badges || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [token]);
  
  const handleStartCampaign = async (campaignId) => {
    try {
      await axios.post(
        `${API}/campaigns/${campaignId}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Campaign started! Complete all activities to earn your badge.');
      fetchData();
    } catch (error) {
      console.error('Error starting campaign:', error);
      toast.error('Failed to start campaign');
    }
  };
  
  const handleViewCampaign = async (campaignId) => {
    try {
      const res = await axios.get(
        `${API}/campaigns/${campaignId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedCampaign(res.data.campaign);
      setSelectedProgress(res.data.user_progress);
    } catch (error) {
      console.error('Error fetching campaign details:', error);
      toast.error('Failed to load campaign details');
    }
  };
  
  // Filter campaigns
  const filteredCampaigns = campaigns.filter(c => {
    if (filter === 'active') return c.user_started && !c.user_completed;
    if (filter === 'completed') return c.user_completed;
    return true;
  });
  
  const activeCampaigns = campaigns.filter(c => c.user_started && !c.user_completed);
  const completedCampaigns = campaigns.filter(c => c.user_completed);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-navigate-black flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-navigate-black">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6">
          <Link to="/dashboard" className="flex items-center gap-1 text-zinc-400 hover:text-white transition-colors">
            <Home className="h-4 w-4" />
            Dashboard
          </Link>
          <ChevronRight className="h-4 w-4 text-zinc-600" />
          <span className="text-white">Learning Campaigns</span>
        </nav>
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Learning Campaigns</h1>
          <p className="text-zinc-400">
            Complete campaign activities to earn exclusive badges and bonus XP
          </p>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-white">{campaigns.length}</div>
            <div className="text-sm text-zinc-500">Available</div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-blue-400">{activeCampaigns.length}</div>
            <div className="text-sm text-zinc-500">In Progress</div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-green-400">{completedCampaigns.length}</div>
            <div className="text-sm text-zinc-500">Completed</div>
          </div>
        </div>
        
        {/* Earned Badges */}
        <EarnedBadgesSection badges={earnedBadges} />
        
        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['all', 'active', 'completed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                filter === f 
                  ? 'bg-navigate-gold text-black' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              {f === 'all' ? 'All Campaigns' : f}
            </button>
          ))}
        </div>
        
        {/* Campaigns Grid */}
        {filteredCampaigns.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCampaigns.map((campaign) => (
              <CampaignCard
                key={campaign.campaign_id}
                campaign={campaign}
                onStart={handleStartCampaign}
                onView={handleViewCampaign}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Target className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-zinc-400 mb-2">
              {filter === 'all' ? 'No campaigns available' : `No ${filter} campaigns`}
            </h3>
            <p className="text-zinc-600">
              {filter === 'all' 
                ? 'Check back later for new learning campaigns'
                : filter === 'active' 
                  ? 'Start a campaign to see it here'
                  : 'Complete a campaign to earn badges'}
            </p>
          </div>
        )}
        
        {/* Campaign Detail Modal */}
        {selectedCampaign && (
          <CampaignDetailModal
            campaign={selectedCampaign}
            progress={selectedProgress}
            onClose={() => {
              setSelectedCampaign(null);
              setSelectedProgress(null);
            }}
          />
        )}
      </div>
    </div>
  );
}
